import { useCallback } from 'react';
import { supabaseData } from '../../utils/supabase-data-client';
import { useWebAuthn } from '../../contexts/WebAuthnContext';

const BUCKET = 'note-images';
const MAX_BYTES = 5 * 1024 * 1024; // 5MB
const ALLOWED = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

const EXT: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/gif': 'gif',
  'image/webp': 'webp',
};

interface UseNoteImageUploadReturn {
  /** Uploads an image for a note and returns its public URL (or '' on failure). */
  uploadImage: (file: File, noteId: string) => Promise<string>;
}

function randomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Uploads note images to the `note-images` Storage bucket and returns their
 * public URL for embedding in note HTML. Errors surface via the snackbar; the
 * editor inserts nothing on failure.
 */
export const useNoteImageUpload = (): UseNoteImageUploadReturn => {
  const { setSnackbar } = useWebAuthn();

  const uploadImage = useCallback(
    async (file: File, noteId: string): Promise<string> => {
      if (!ALLOWED.includes(file.type)) {
        setSnackbar({
          open: true,
          message: 'Unsupported image type. Use PNG, JPG, GIF, or WEBP.',
          severity: 'error',
        });
        return '';
      }
      if (file.size > MAX_BYTES) {
        setSnackbar({
          open: true,
          message: 'Image is too large (max 5MB).',
          severity: 'error',
        });
        return '';
      }

      const ext = EXT[file.type] ?? 'png';
      const path = `${noteId}/${randomId()}.${ext}`;

      const { error } = await supabaseData.storage
        .from(BUCKET)
        .upload(path, file, {
          contentType: file.type,
          upsert: false,
        });

      if (error) {
        setSnackbar({
          open: true,
          message: 'Image upload failed. Please try again.',
          severity: 'error',
        });
        return '';
      }

      const { data } = supabaseData.storage.from(BUCKET).getPublicUrl(path);
      return data.publicUrl ?? '';
    },
    [setSnackbar],
  );

  return { uploadImage };
};
